import { useEffect, useState } from 'react';
import {
  ICureDto,
  IProductDto,
  IRoomCategoryDto,
  ISpecialPackageDto,
  ITreatmentDto,
} from '../model';
import { CureApi, ICureApi, IProductApi, ProductApi } from '../services';
import { IRoomCategoryApi, RoomCategoryApi } from '../services/roomCategoryApi';
import { ITreatmentApi, TreatmentApi } from '../services/treatmentApi';

export interface ISpecialPackageReferenceData {
  roomCategoryById: Record<number, IRoomCategoryDto>;
  cureById: Record<number, ICureDto>;
  productById: Record<number, IProductDto>;
  treatmentById: Record<number, ITreatmentDto>;
  isLoading: boolean;
}

const emptyReferenceData: ISpecialPackageReferenceData = {
  roomCategoryById: {},
  cureById: {},
  productById: {},
  treatmentById: {},
  isLoading: false,
};

function toRecord<T extends { id: number }>(items: T[]): Record<number, T> {
  return Object.fromEntries(items.map((item) => [item.id, item]));
}

async function loadDistinctIds<T>(
  ids: number[],
  loader: (id: number) => Promise<T>
): Promise<T[]> {
  const distinctIds = [...new Set(ids)];

  if (distinctIds.length === 0) {
    return [];
  }

  return Promise.all(distinctIds.map((id) => loader(id)));
}

export function useSpecialPackageReferences(
  specialPackage?: ISpecialPackageDto
): ISpecialPackageReferenceData {
  const [referenceData, setReferenceData] =
    useState<ISpecialPackageReferenceData>(emptyReferenceData);

  useEffect(() => {
    if (!specialPackage) {
      setReferenceData(emptyReferenceData);
      return;
    }

    let isCancelled = false;

    const roomCategoryApi: IRoomCategoryApi = new RoomCategoryApi();
    const cureApi: ICureApi = new CureApi();
    const productApi: IProductApi = new ProductApi();
    const treatmentApi: ITreatmentApi = new TreatmentApi();

    setReferenceData((current) => ({
      ...current,
      isLoading: true,
    }));

    Promise.all([
      loadDistinctIds(
        specialPackage.specialPackageRoomCategoryList.map(
          (item) => item.roomCategoryId
        ),
        (id) => roomCategoryApi.getById(id)
      ),
      loadDistinctIds(
        specialPackage.specialPackageCureList.map((item) => item.cureId),
        (id) => cureApi.getById(id)
      ),
      loadDistinctIds(
        specialPackage.specialPackageProductList.map((item) => item.productId),
        (id) => productApi.getById(id)
      ),
      loadDistinctIds(
        specialPackage.specialPackageTreatmentList.map(
          (item) => item.treatmentId
        ),
        (id) => treatmentApi.getById(id)
      ),
    ])
      .then(([roomCategories, cures, products, treatments]) => {
        if (isCancelled) {
          return;
        }

        setReferenceData({
          roomCategoryById: toRecord(roomCategories),
          cureById: toRecord(cures),
          productById: toRecord(products),
          treatmentById: toRecord(treatments),
          isLoading: false,
        });
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setReferenceData({
          ...emptyReferenceData,
          isLoading: false,
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [specialPackage]);

  return referenceData;
}

export default useSpecialPackageReferences;
